import { FC, MouseEvent, RefObject } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import Icon from 'components/ui/icon';
import Loader from 'components/ui/loader';
import statuses from './statuses';
import InfoIcon from 'components/ui/infoIcon';
import ConditionalWrapper from 'components/ui/conditionalWrapper';
import './button.scss';

interface Props {
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  id?: string;
  text?: string | JSX.Element;
  link?: string;
  status?: keyof typeof statuses;
  icon?: string;
  iconSize?: number;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  customClass?: any;
  type?: 'button' | 'submit' | 'reset';
  size?: 'x-large' | 'large' | 'normal' | 'small' | 'inline' | number;
  tooltip?: string;
  infoTooltip?: string;
  tooltipDelay?: number;
  tooltipId?: string;
  buttonRef?: RefObject<HTMLButtonElement>;
}

const Button: FC<Props> = ({
  id,
  text,
  link,
  disabled,
  loading,
  icon,
  iconSize,
  customClass,
  tooltip,
  tooltipId,
  infoTooltip,
  tooltipDelay,
  buttonRef,
  onClick,
  status = 'primary',
  iconPosition = 'right',
  type = 'button',
  size = 'normal',
}) => {
  const history = useHistory();

  const classes = classNames('ui-button', customClass, {
    [`ui-button_status-${status}`]: !!status,
    [`ui-button_size-${size}`]: !!size,
    'ui-button_disabled': disabled && !loading,
    'ui-button_loading': loading,
    'ui-button_only-icon': !text && icon,
    [`ui-button_${iconPosition}-icon`]: !!iconPosition,
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      return;
    }

    if (link) {
      history.push(link);
    }

    if (onClick) {
      onClick(event);
    }
  };

  return (
    <ConditionalWrapper
      condition={disabled && tooltip}
      wrap={(children) => (
        <div
          data-tip={tooltip}
          className='ui-button-wrapper'
          data-for={tooltipId}>
          {children}
        </div>
      )}>
      {
        <button
          ref={buttonRef}
          id={id}
          type={type}
          className={classes}
          onClick={handleClick}
          data-tip={tooltip}
          data-for={tooltipId}
          data-delay-show={tooltipDelay}
          disabled={disabled}>
          {loading && (
            <Loader customClass='ui-button__loader ui-loader_button' />
          )}
          {text && (
            <span className='ui-button__text'>
              {text}
              {infoTooltip && (
                <InfoIcon
                  tooltip={infoTooltip}
                  size={9}
                  customClass='ui-button__info'
                  dataPlace='top'
                />
              )}
            </span>
          )}
          {icon && (
            <Icon
              name={icon}
              size={iconSize || 16}
              className={classNames('ui-button__icon', { rotating: loading })}
            />
          )}
        </button>
      }
    </ConditionalWrapper>
  );
};

export default Button;
